import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  useProfile,
  useAuditSummary,
  SyncStatus,
  TaxJurisdiction,
  fiatCurrencyCodes,
} from "@moonscape/shared";
import { useBridgeMatches } from "@/hooks/bridge/useBridgeMatches";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Callout, DHead, Fig, Tag, TagTone } from "@/components/ms";
import { HeadLink } from "./chrome";

interface Alert {
  id: string;
  type: "critical" | "warning" | "info";
  category: string;
  title: string;
  description: string;
  count?: number;
  action: {
    label: string;
    href?: string;
    onClick?: () => void;
  };
  dismissible?: boolean;
}

const ALERT_TONE: Record<Alert["type"], TagTone> = {
  critical: "neg",
  warning: "warn",
  info: "neu",
};

export const AlertsCenterWidget = () => {
  const { profile } = useProfile();
  const { data: auditSummaryData } = useAuditSummary();
  const { data: bridgeMatchesData } = useBridgeMatches(0, { status: 'suggested' });

  // Track dismissed alerts in component state (could be moved to localStorage)
  const [dismissedAlerts, setDismissedAlerts] = useState<Set<string>>(new Set());

  // Build alerts from various data sources
  const alerts: Alert[] = useMemo(() => {
    const alertList: Alert[] = [];

    // 1. Broken Connections
    const linkedExchanges = profile?.linkedExchangeAccounts || [];
    const exchangesArray = Array.isArray(linkedExchanges)
      ? linkedExchanges
      : Object.values(linkedExchanges || {});

    const failedExchanges = exchangesArray.filter(
      (ex: any) => ex.syncStatus === SyncStatus.FAILED
    );

    if (failedExchanges.length > 0) {
      failedExchanges.forEach((exchange: any) => {
        alertList.push({
          id: `broken-connection-${exchange.id}`,
          type: "critical",
          category: "Connection failed",
          title: `${exchange.name || "Exchange"} API key expired`,
          description: "Update your API credentials to resume syncing transactions",
          action: {
            label: "Reconnect",
            href: `/settings/exchange/${exchange.id}`,
          },
        });
      });
    }

    // 2. Bridge Transfers Needing Review
    const bridgeCount = (bridgeMatchesData?.items?.length || 0) + (bridgeMatchesData?.remaining || 0);
    if (bridgeCount > 0) {
      alertList.push({
        id: "bridge-matches-pending",
        type: "warning",
        category: "Bridge transfers",
        title: `${bridgeCount} bridge transfer${bridgeCount !== 1 ? 's' : ''} need confirmation`,
        description: "Unmatched cross-chain transfers may cause phantom disposals",
        count: bridgeCount,
        action: {
          label: "Review Bridges",
          href: "/bridge-matches",
        },
      });
    }

    // 3. Audit Flags
    const auditSummary = auditSummaryData?.data?.summary;
    if (auditSummary) {
      if (auditSummary.must_fix > 0) {
        alertList.push({
          id: "audit-must-fix",
          type: "critical",
          category: "Tax audit",
          title: `${auditSummary.must_fix} critical transaction issue${auditSummary.must_fix !== 1 ? 's' : ''}`,
          description: "Missing cost basis or incorrect categories affecting tax calculations",
          count: auditSummary.must_fix,
          action: {
            label: "Fix Issues",
            href: "/audit?priority=must_fix",
          },
        });
      }

      if (auditSummary.should_fix > 0) {
        alertList.push({
          id: "audit-should-fix",
          type: "warning",
          category: "Tax audit",
          title: `${auditSummary.should_fix} transaction${auditSummary.should_fix !== 1 ? 's' : ''} should be reviewed`,
          description: "Potential issues that could impact your tax accuracy",
          count: auditSummary.should_fix,
          action: {
            label: "Review",
            href: "/audit?priority=should_fix",
          },
          dismissible: true,
        });
      }
    }

    // 4. Tax Settings Not Configured
    if (!profile?.taxJurisdiction) {
      alertList.push({
        id: "tax-settings-missing",
        type: "critical",
        category: "Tax settings",
        title: "Tax jurisdiction not set",
        description: "Select your country to enable accurate tax calculations",
        action: {
          label: "Set Country",
          href: "/settings/tax",
        },
      });
    }

    const baseCurrency = profile?.baseCurrency?.toUpperCase();
    const hasUnsupportedBaseCurrency =
      !!profile?.taxJurisdiction &&
      (!baseCurrency || !fiatCurrencyCodes.includes(baseCurrency));

    if (hasUnsupportedBaseCurrency) {
      alertList.push({
        id: "tax-settings-base-currency",
        type: "critical",
        category: "Tax settings",
        title: "Base currency unsupported for pricing",
        description:
          "Your tax jurisdiction currency isn't available in fiat rates, so pricing will fail.",
        action: {
          label: "Review Tax Settings",
          href: "/settings/tax",
        },
      });
    }

    if (profile?.taxJurisdiction && !profile?.accountingMethod) {
      alertList.push({
        id: "tax-settings-accounting-method",
        type: "warning",
        category: "Tax settings",
        title: "Accounting method not set",
        description:
          "Tax calculations will default to FIFO unless you select a method.",
        action: {
          label: "Set Method",
          href: "/settings/tax",
        },
      });
    }

    if (
      profile?.taxJurisdiction === TaxJurisdiction.US &&
      !profile?.taxFilingStatus
    ) {
      alertList.push({
        id: "tax-settings-filing-status",
        type: "warning",
        category: "Tax settings",
        title: "Tax filing status missing",
        description:
          "Filing status is required to apply correct US capital gains rates.",
        action: {
          label: "Set Filing Status",
          href: "/settings/tax",
        },
      });
    }

    // Filter out dismissed alerts
    return alertList.filter(alert => !dismissedAlerts.has(alert.id));
  }, [profile, auditSummaryData, bridgeMatchesData, dismissedAlerts]);

  // Handle dismiss
  const handleDismiss = (alertId: string) => {
    setDismissedAlerts(prev => new Set(prev).add(alertId));
    // TODO: Persist to localStorage or user preferences
  };

  // No alerts state
  if (alerts.length === 0) {
    return (
      <section className="flex flex-col">
        <DHead label="Open questions" />
        <Callout className="mt-3.5">
          <b>Nothing needs you.</b> Every source is syncing and no transaction is
          waiting on an answer.
        </Callout>
      </section>
    );
  }

  // Count by severity
  const criticalCount = alerts.filter(a => a.type === "critical").length;

  return (
    <section className="flex flex-col">
      <DHead
        label="Open questions"
        right={<HeadLink to="/audit">Answer them</HeadLink>}
      />

      <Callout tone="tan" className="mt-3.5">
        <b>
          {alerts.length} thing{alerts.length !== 1 ? "s" : ""} are holding your number
          back.
        </b>{" "}
        {criticalCount > 0
          ? `${criticalCount} of them change the figures you would file.`
          : "None of them are critical — clearing them tightens the picture."}
      </Callout>

      <div className="mt-3.5 flex flex-col">
        {alerts.map((alert) => (
          <div
            key={alert.id}
            className="flex items-center gap-3 border-t border-ms-line first:border-t-0"
          >
            <Fig
              className="min-w-0 flex-1 border-t-0"
              label={alert.title}
              sub={
                <>
                  {alert.category} · {alert.description}
                </>
              }
              tag={<Tag tone={ALERT_TONE[alert.type]}>{alert.count ?? alert.category}</Tag>}
              value={
                alert.action.href ? (
                  <Link
                    to={alert.action.href}
                    className="text-[11px] font-normal uppercase tracking-[0.13em] text-ms-link transition-colors hover:text-ms-ink"
                  >
                    {alert.action.label} →
                  </Link>
                ) : (
                  <button
                    type="button"
                    onClick={alert.action.onClick}
                    className="text-[11px] font-normal uppercase tracking-[0.13em] text-ms-link transition-colors hover:text-ms-ink"
                  >
                    {alert.action.label} →
                  </button>
                )
              }
            />
            {alert.dismissible && (
              <button
                type="button"
                onClick={() => handleDismiss(alert.id)}
                className="grid h-8 w-8 flex-none place-items-center text-ms-dim transition-colors hover:text-ms-ink"
                title="Dismiss for now"
                aria-label="Dismiss alert"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        ))}
      </div>

      <p className="mt-4 text-[10px] leading-[1.45] text-ms-dim">
        Stuck on one?{" "}
        <a
          href="https://help.moonscape.app"
          target="_blank"
          rel="noopener noreferrer"
          className="text-ms-link hover:text-ms-green"
        >
          Troubleshooting guides
        </a>
        {" · "}
        <a href="mailto:hello@moonscape.app" className="text-ms-link hover:text-ms-green">
          contact support
        </a>
      </p>
    </section>
  );
};
